import moment from 'moment-timezone';
import { env } from './env';

const TZ = env.TIMEZONE;

// Bahasa Indonesia day/month names. We map these ourselves (rather than
// moment.locale('id')) because Vite's dep pre-bundling registers the locale on
// a separate moment copy, so the global locale silently falls back to English.
// moment stays responsible for timezone + numeric parts (locale-independent).
const ID_DAYS = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
const ID_MONTHS = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
];

/** Format a number as Indonesian Rupiah, no decimals. */
export function rupiah(value: number | null | undefined): string {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

/** Format a plain integer with id-ID grouping (no currency symbol). */
export function number(value: number | null | undefined): string {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('id-ID').format(n);
}

/** Today's date in the configured timezone as YYYY-MM-DD. */
export function todayISO(): string {
  return moment.tz(TZ).format('YYYY-MM-DD');
}

/** Add days to today (timezone-aware) and return YYYY-MM-DD. */
export function addDaysISO(days: number): string {
  return moment.tz(TZ).add(days, 'days').format('YYYY-MM-DD');
}

/** Compact numeric date for form fields (e.g. "05-07-2026"). */
export function formatDMY(iso: string | undefined | null): string {
  if (!iso) return '';
  return moment.tz(iso, TZ).format('DD-MM-YYYY');
}

/** Human-readable date in Bahasa Indonesia (e.g. "Min, 05 Jul 2026"). */
export function formatDate(iso: string | undefined | null): string {
  if (!iso) return '—';
  const m = moment.tz(iso, TZ);
  return `${ID_DAYS[m.day()]}, ${m.format('DD')} ${ID_MONTHS[m.month()]} ${m.format('YYYY')}`;
}

/** Human-readable date+time in Bahasa Indonesia (e.g. "05 Jul 2026 · 05:43"). */
export function formatDateTime(iso: string | undefined | null): string {
  if (!iso) return '—';
  const m = moment.tz(iso, TZ);
  return `${m.format('DD')} ${ID_MONTHS[m.month()]} ${m.format('YYYY')} · ${m.format('HH:mm')}`;
}

/** Time-only (HH:mm) for slot windows. */
export function formatTime(iso: string | undefined | null): string {
  if (!iso) return '—';
  return moment.tz(iso, TZ).format('HH:mm');
}

/** Is the given YYYY-MM-DD within today..+maxDays (timezone-aware)? */
export function isWithinRange(dateISO: string, maxDays = 7): boolean {
  const d = moment.tz(dateISO, 'YYYY-MM-DD', TZ).startOf('day');
  const start = moment.tz(TZ).startOf('day');
  const end = moment.tz(TZ).add(maxDays, 'days').endOf('day');
  return d.isSameOrAfter(start) && d.isSameOrBefore(end);
}
